// Radha — invoice discounting tracker (migration 050). This is a
// system-of-record, not an integration: RXIL/M1xchange/Invoicemart have no
// public developer API to automate against, so the AR team submits an
// invoice there themselves (on that platform's own portal) and logs the
// outcome here so Radha's own books stay in sync.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";

export type DiscountingPlatform = "treds_rxil" | "treds_m1xchange" | "treds_invoicemart" | "bank_factoring" | "other";
export type DiscountingStatus = "submitted" | "bid_received" | "discounted" | "rejected" | "settled";

export interface InvoiceDiscounting {
  id: string; invoice_id: string; platform: DiscountingPlatform; status: DiscountingStatus;
  discount_rate_pct: string | null; financier_name: string | null; net_amount_received: string | null;
  submitted_at: string; discounted_at: string | null; notes: string | null;
}

export function useInvoiceDiscountingList() {
  return useQuery({
    queryKey: ["invoice-discounting"],
    queryFn: async (): Promise<InvoiceDiscounting[]> => {
      const { data, error } = await supabase
        .from("invoice_discounting")
        .select("id, invoice_id, platform, status, discount_rate_pct, financier_name, net_amount_received, submitted_at, discounted_at, notes")
        .order("submitted_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 15_000,
  });
}

export function useSubmitForDiscounting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { invoice_id: string; platform: DiscountingPlatform; notes?: string }) => {
      const { error } = await supabase.from("invoice_discounting").insert(input);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invoice-discounting"] }),
  });
}

export function useUpdateDiscounting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string; status?: DiscountingStatus; discount_rate_pct?: number; financier_name?: string; net_amount_received?: number; discounted_at?: string; notes?: string }) => {
      const { error } = await supabase.from("invoice_discounting").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invoice-discounting"] }),
  });
}

export function discountingErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : (err && typeof err === "object" && "message" in err ? String((err as { message: unknown }).message) : String(err));
  const m = msg.match(/^([A-Z_]+):\s*(.*)$/);
  return m ? m[2] || m[1] : msg;
}
